import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import SummaryActionCard from "../../../../../../components/app-specific/summary-cards/summary-action-card";
import InventoryTransferBatchInformationViewItems from "./items";
import TransferOperatorsProvider from "./use-transfer-operators";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import {Link, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import Api from "../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../core/services/toast";
import DateUtils from "../../../../../../../core/services/date-utils";
import {MenuItem, TextField} from "@mui/material";
import {InventoryTransferStatuses, InventoryTransferStatusList} from "../../../../../../../core/constants/enums";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";

const summaryBodyIds = {
    submitDateTime: 'submitDateTime',
    modifiedDateTime: 'modifiedDateTime',
    requiredDate: 'requiredDate',
    createdByName: 'createdByName',
    totalPartCount: 'totalPartCount',
    totalRequestedQty: 'totalRequestedQty',
    totalScannedQty: 'totalScannedQty',
    externalId: 'externalId',
};

const actionsSummaryBodyIds = {
    status: 'status',
    fromWarehouse: 'fromWarehouse',
    toWarehouse: 'toWarehouse',
};

const InventoryTransferBatchInformationView = () => {
    const params = useParams();
    const {t} = useTranslation('views', {keyPrefix: 'inventory-transfer.batch-information'});
    const [loading, setLoading] = useState(true);
    const [transfer, setTransfer] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('inventory-transfer-batch-info-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Configures notebook tags and behavioral analytics for the transfer information view.
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.inventoryTransferBatchInformation(params.id));
        configureBehavioralTool((tags) => tags.inventoryTransferBatchInformation(params.id));
    }, [configureBehavioralTool, configureNotebookTags, params.id]);

    const transferId = useMemo(() => Number(params.id), [params.id]);

    const canEditStatus = useMemo(
        () => transfer?.transferStatusName !== InventoryTransferStatuses.posted
            && transfer?.transferStatusName !== InventoryTransferStatuses.finalized,
        [transfer?.transferStatusName],
    );

    const editableStatuses = useMemo(
        () => InventoryTransferStatusList.filter((status) => status !== InventoryTransferStatuses.posted),
        [],
    );

    const totalPartCount = useMemo(() => transfer?.items?.length ?? 0, [transfer?.items]);

    const totalRequestedQty = useMemo(
        () => transfer?.items?.reduce((sum, item) => sum + (item.requestedQty ?? 0), 0) ?? 0,
        [transfer?.items],
    );

    const totalScannedQty = useMemo(
        () => transfer?.items?.reduce((sum, item) => sum + (item.totalScannedCount ?? 0), 0) ?? 0,
        [transfer?.items],
    );

    const canPost = useMemo(() => {
        return !!transfer
            && transfer.transferStatusName === InventoryTransferStatuses.finalized
            && operatorAccessLevels.packages.bizInventory.submitTransfer;
    }, [operatorAccessLevels.packages.bizInventory.submitTransfer, transfer]);

    /**
     * Formats a server date-time value for display.
     */
    const formatDateTime = useCallback((value) => {
        if (!value) {
            return '--';
        }
        return DateUtils.serverToLocal(value).format(DateUtils.dateTimeFormat);
    }, []);

    /**
     * Formats a server date value for display.
     */
    const formatDate = useCallback((value) => {
        if (!value) {
            return '--';
        }
        return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
    }, []);

    /**
     * Fetches transfer detail from the API.
     * @param {boolean} [showLoading=false] When true, toggles the page loading state (initial load only).
     */
    const getTransferInformation = useCallback(async (showLoading = false) => {
        if (showLoading) {
            setLoading(true);
        }
        const response = await Api.getInventoryTransfer(transferId);
        if (!isMounted()) {
            return;
        }
        if (showLoading) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            setTransfer(response.data ?? null);
        }
    }, [isMounted, transferId]);

    /**
     * Loads transfer detail when the transfer id changes.
     */
    useEffect(() => {
        getTransferInformation(true).then();
    }, [getTransferInformation]);

    /**
     * Posts the transfer directly through the API.
     */
    const postTransfer = useCallback(async () => {
        const response = await Api.postInventoryTransfer(transferId);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            await getTransferInformation();
        }
    }, [getTransferInformation, isMounted, transferId]);

    /**
     * Updates the transfer status through the API.
     */
    const updateTransferStatus = useCallback(async (statusName) => {
        setUpdatingStatus(true);
        const response = await Api.updateInventoryTransferStatus({
            transferId,
            statusName,
        });
        if (!isMounted()) {
            return;
        }
        setUpdatingStatus(false);
        if (response?.resultFlag) {
            await getTransferInformation();
        }
    }, [getTransferInformation, isMounted, transferId]);

    /**
     * Prompts for confirmation before finalizing, then updates status on approval.
     */
    const onTransferStatusChange = useCallback((event) => {
        const statusName = event.target.value;
        if (statusName === InventoryTransferStatuses.finalized) {
            // A transfer with nothing scanned/committed cannot be posted later, so block finalizing early.
            if (totalScannedQty <= 0) {
                ToastService.warning({
                    title: t('finalizeEmptyWarningTitle'),
                    message: t('finalizeEmptyWarning'),
                });
                return;
            }
            window.partner?.openConfirmationDialog(
                <div className={'p-3'}>
                    <p className={'text-sm text-color-2'}>
                        {t('finalizeConfirmation')}
                    </p>
                </div>,
                {
                    maxWidth: 'sm',
                },
                (confirmed) => confirmed && updateTransferStatus(statusName),
            );
            return;
        }
        void updateTransferStatus(statusName);
    }, [t, totalScannedQty, updateTransferStatus]);

    const orderInfoSummary = useMemo(() => ({
        header: {
            title: t('transferNo'),
            value: transfer?.code,
            loadingValue: t('loading'),
            copyable: true,
        },
        body: [
            {
                id: summaryBodyIds.submitDateTime,
                title: t('submissionDate'),
                value: formatDateTime(transfer?.submitDateTime),
            },
            {
                id: summaryBodyIds.modifiedDateTime,
                title: t('modifiedDate'),
                value: formatDateTime(transfer?.modifiedDateTime),
            },
            {
                id: summaryBodyIds.requiredDate,
                title: t('requiredDate'),
                value: formatDate(transfer?.requiredDate),
            },
            {
                id: summaryBodyIds.createdByName,
                title: t('createdBy'),
                value: transfer?.createdByName ?? '--',
            },
            {
                id: summaryBodyIds.totalPartCount,
                title: t('totalProductCount'),
                value: totalPartCount,
            },
            {
                id: summaryBodyIds.totalRequestedQty,
                title: t('totalRequestedQty'),
                value: totalRequestedQty,
            },
            {
                id: summaryBodyIds.totalScannedQty,
                title: t('totalScannedProduct'),
                value: totalScannedQty,
            },
            ...(transfer?.externalId ? [{
                id: summaryBodyIds.externalId,
                title: t('externalId'),
                value: transfer.externalId,
            }] : []),
        ],
    }), [formatDate, formatDateTime, t, totalPartCount, totalRequestedQty, totalScannedQty, transfer]);

    const actionsSummary = useMemo(() => ({
        header: {
            title: t('status'),
            value: transfer?.transferStatusName,
            loadingValue: t('loading'),
        },
        body: [
            ...(canEditStatus ? [{
                id: actionsSummaryBodyIds.status,
                title: t('status'),
                freeFormValue: true,
                value:
                    <TextField
                        select
                        size={'small'}
                        fullWidth
                        value={transfer?.transferStatusName ?? ''}
                        disabled={updatingStatus || loading}
                        onChange={onTransferStatusChange}
                    >
                        {
                            editableStatuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))
                        }
                    </TextField>,
            }] : []),
            {
                id: actionsSummaryBodyIds.fromWarehouse,
                title: t('sourceWarehouse'),
                value: `${transfer?.fromWarehouseName ?? ''} (${transfer?.fromWarehouseCode ?? ''})`,
                loadingValue: t('loading'),
            },
            {
                id: actionsSummaryBodyIds.toWarehouse,
                title: t('destinationWarehouse'),
                value: `${transfer?.toWarehouseName ?? ''} (${transfer?.toWarehouseCode ?? ''})`,
                loadingValue: t('loading'),
            },
        ],
        actions: [
            {
                id: 'postTransfer',
                value: t('postTransfer'),
                submittingValue: t('postingTransfer'),
                show: canPost,
                variant: 'contained',
                onClick: postTransfer,
            },
        ],
    }), [canEditStatus, canPost, editableStatuses, loading, onTransferStatusChange, postTransfer, t, transfer, updatingStatus]);

    return (
        <TransferOperatorsProvider>
            <div className={'batch-information-view'}>
                <Row className={'no-gutters w-100 align-items-center justify-content-between'}>
                <Row className={'justify-content-start align-items-center no-gutters'}>
                    <IconButton
                        component={Link}
                        to={routes.inventory.inventoryTransfer.list}
                        replace
                        color={'paper.secondary'}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <p className={'text-lg font-weight-semi-bold ml-2'}>
                        {t('title')}
                    </p>
                </Row>
                <div className={'w-100 my-3'}/>
                <div className={'d-flex flex-wrap w-100 justify-content-center'}>
                    <Col xs={12} md={6} className={'mb-3'}>
                        <SummaryInfoCard
                            header={orderInfoSummary.header}
                            body={orderInfoSummary.body}
                            loading={loading}
                            loadingCount={orderInfoSummary.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} className={'mb-3'} style={{overflowY: 'hidden'}}>
                        <SummaryActionCard
                            className={'auto-height'}
                            header={actionsSummary.header}
                            body={actionsSummary.body}
                            actions={actionsSummary.actions}
                            loadingCount={actionsSummary.body.length}
                            loading={loading}
                        />
                    </Col>
                </div>
                <div className={'w-100 my-2'}/>
                <InventoryTransferBatchInformationViewItems
                    transfer={transfer}
                    loading={loading}
                    setTransfer={setTransfer}
                    onItemsChanged={getTransferInformation}
                />
                <div className={'my-3 w-100'}/>
            </Row>
            </div>
        </TransferOperatorsProvider>
    );
};

export default InventoryTransferBatchInformationView;
